import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "@/services/authService";
import { usersService } from "@/services/usersService";
import type { LoginRequest, RegisterRequest } from "@/types/auth";
import { queryKeys } from "./keys";

export function useUserContext(enabled = true) {
  return useQuery({
    queryKey: queryKeys.auth.context,
    queryFn: () => usersService.getMyContext(),
    enabled,
    retry: false,
  });
}

export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: LoginRequest) => authService.login(request),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.context });
    },
  });
}

export function useRegisterMutation() {
  return useMutation({
    mutationFn: (request: RegisterRequest) => authService.register(request),
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
  });
}

export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: ({ email, token, newPassword }: { email: string; token: string; newPassword: string }) =>
      authService.resetPassword(email, token, newPassword),
  });
}

export function useOtpConfig() {
  return useQuery({
    queryKey: ["auth", "otp-config"] as const,
    queryFn: () => authService.getOtpConfig(),
    staleTime: 60_000,
  });
}

export function useSendLoginOtpMutation() {
  return useMutation({
    mutationFn: (phoneNumber: string) => authService.sendLoginOtp(phoneNumber),
  });
}
